import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ZipcodeService } from '@webmodule-order/api';
import { map, mergeMap } from 'rxjs';
import { kebabCase } from 'lodash';
import { OrderTypeEnum, ZipCodeCheckDto, ZipCodeCheckResultDto } from '../dto';
import { ClientOrderService, ClientPackageService } from '../services';
import { CartService } from '../services/cart/cart.service';
import { AlertComponent } from '../components/alert/alert.component';
import { NgClass } from '@angular/common';
import { PanelComponent } from '../panel/panel.component';
import { TranslatePipe } from '@ngx-translate/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';

const ZIPCODE_PATTERN = /^[1-9]\d{3} ?(?!sa|sd|ss)[a-zA-Z]{2}$/;

interface ZipCodeForm {
  zipcode: FormControl<string>;
  houseNumber: FormControl<number | null>;
  addition: FormControl<string | null>;
}

interface RequestResponse {
  status: number;
  message: string;
  available: boolean;
}

@Component({
  selector: 'webmodule-order-zipcode-check',
  templateUrl: './zipcode-check.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PanelComponent, AlertComponent, ReactiveFormsModule, NgClass, TranslatePipe],
  standalone: true,
})
export class ZipcodeCheckComponent {
  private readonly zipcodeService = inject(ZipcodeService);
  private readonly clientOrderService = inject(ClientOrderService);
  private readonly cartService = inject(CartService);
  private readonly clientPackageService = inject(ClientPackageService);
  private readonly orderableTypes = ['pre-order', 'orderable', 'in-use-by-self', 'in-use-by-other'];
  private readonly gtmService = inject(GoogleTagManagerService);
  /**
   * Testcase
   * 5361TM - 62 (2 reponse, orderable / not present )
   * 7841BW - 10 (2 reponse, orderable / not present )
   * 2481KJ - 15 (1 response, not present)
   * 7764AC - 2
   */
  readonly form = new FormGroup<ZipCodeForm>({
    zipcode: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(ZIPCODE_PATTERN)],
    }),
    houseNumber: new FormControl(null, { nonNullable: true, validators: [Validators.required] }),
    addition: new FormControl(null),
  });
  requestResponse = signal<RequestResponse | null>(null);
  hasSessionResult = signal<boolean>(false);
  address = this.clientOrderService.address;
  supplier = this.clientOrderService.supplier;
  selectedType = this.clientOrderService.orderType;
  loading = this.clientOrderService.loading;
  loaded = this.clientOrderService.loaded;

  constructor() {
    this.clientOrderService
      .loadSessionState('orderState')
      .then((session) => {
        const zipcodeResults = session.zipcodeResult;

        if (zipcodeResults) {
          this.clientPackageService.loadSessionState('packageState').then((packages) => {
            this.setResult(zipcodeResults);
            this.hasSessionResult.set(true);
            this.clientPackageService.setState(packages);
            this.pushTag({ event: 'continueConfigurationFromSession' });
          });
        }
      })
      .catch(() => console.log('no session'));

    this.form.patchValue({ zipcode: '7764AC', houseNumber: 2 });
    // this.form.patchValue({ zipcode: '9341AE', houseNumber: 1 });
    // this.form.patchValue({ zipcode: '9104CP', houseNumber: 26, addition: 'a' });
  }

  reset() {
    this.hasSessionResult.set(false);
    this.requestResponse.set(null);
    this.clientOrderService.reset();
    this.clientPackageService.reset();
    this.cartService.resetCart();
  }

  check() {
    this.requestResponse.set(null);
    this.hasSessionResult.set(false);

    this.clientOrderService.setLoading(true);

    const data = this.form.value;
    let resultDto: ZipCodeCheckResultDto;

    const gtmEvent = {
      event: 'zipcodeSubmit',
      zipcode: `${data.zipcode} ${data.houseNumber} ${data.addition}`,
    };
    this.pushTag(gtmEvent);

    this.zipcodeService
      .postZipcodeCheck({ postNLOnly: false, body: data as ZipCodeCheckDto })
      .pipe(
        map((res) => {
          return res.filter((checkResult) => this.orderableTypes.includes(kebabCase(checkResult.availability)));
        }),
        map((res: ZipCodeCheckResultDto[]) => {
          if (!res.length) {
            throw new Error('Helaas! Wij kunnen u geen glasvezel leveren op het door u opgegeven adres.');
          }
          resultDto = res[0];

          if (!resultDto || !resultDto?.location || !resultDto.postcodeNL || !resultDto?.location.deliveryArea) {
            throw new Error('Helaas! Wij kunnen u geen glasvezel leveren op het door u opgegeven adres.');
          }
          return resultDto;
        }),
        mergeMap((resultDto: ZipCodeCheckResultDto) => {
          if (!resultDto?.location?.id) {
            throw new Error('Helaas! Wij kunnen u geen glasvezel leveren op het door u opgegeven adres.');
          }
          return this.clientPackageService.fetchPackages(resultDto.location.id);
        }),
      )
      .subscribe({
        next: () => this.setResult(resultDto),
        error: (error) => {
          if (400 === error.status) {
            error.message = 'De door u opgegeven combinatie van postcode en huisnummer klopt niet, graag even controleren en u kunt het opnieuw proberen.';
          } else if (500 === error.status) {
            error.message = 'Er lijkt iets fout te zijn gegaan, het probleem ligt aan onze kant. Mocht u vragen of ondersteuning nodig hebben kunt u contact opnemen met onze klantenservice.';
          }

          this.pushTag({ event: 'unableToOrder' });
          this.requestResponse.set({ message: error.message, status: error.status ?? 404, available: false });
        },
      })
      .add(() => this.clientOrderService.setLoading(false));
  }

  setType(type: OrderTypeEnum) {
    this.clientOrderService.setType(type);
  }

  private setResult(resultDto: ZipCodeCheckResultDto) {
    this.clientOrderService.setResultDto(resultDto);
    this.requestResponse.set({
      message: `Gefeliciteerd, er is glasvezel beschikbaar op je adres!`,
      status: 200,
      available: true,
    });
    this.pushTag({ event: 'continueConfiguration' });
  }

  private pushTag(gtmEvent: { event: string; [p: string]: any }) {
    this.gtmService.pushTag({ ...gtmEvent, page: 'configuration' }).then(() => console.log('GTM', gtmEvent.event));
  }
}
